// @ts-check
// Ações da automação (contrato 08, seções 3.3 e 3.9): um cartão por ação, em ordem, com o formulário de cada tipo. O
// se/senão mostra os ramos "sim" e "não" com recuo (sem grafo), aninhável até dois níveis. Cada campo de texto tem o
// seletor de variáveis com o gatilho e as saídas dos passos anteriores.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Toggle } from '../../ui/feedback.js';
import { objectsByNameSingular, objectsState } from '../../data/metadata-store.js';
import { listWhatsappTemplates } from '../../data/integrations.js';
import { listChannels } from '../../data/conversations.js';
import { WHATSAPP_TEMPLATES_PATH, channelLabel } from '../../lib/whatsapp-templates.js';
import { templateCategoryLabel } from '../settings/whatsapp-templates/whatsapp-template-card.js';
import { previewAutomationAction } from '../../data/automations.js';
import { useDebouncedValue } from '../../ui/hooks.js';
import { CALENDAR_EVENT_TYPES } from '../../../../shared/calendar/event-types.js';
import {
  ACTIONS_BLOCKED_ON_DELETED_RECORD, ACTIONS_NEEDING_RECORD, ACTION_ICONS, ACTION_TYPES, MAXIMUM_BRANCH_DEPTH, automationObjects, findField, isMemberRelation,
  isRecordActionField, isRecordlessTrigger, isScheduleDateField, isUpdatableField, isWatchableField, pickFields, recordKeyOf,
} from './automation-labels.js';
import { ConditionsEditor, parseConditions, serializeConditions } from './automation-conditions.js';
import { VariableTextField, buildVariableGroups, stepNumbers } from './automation-variables.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('../../data/automations.js').AutomationAction} AutomationAction
 */

/**
 * Ação nova com os valores padrão do tipo. O id nasce aqui para os passos seguintes já poderem usar as variáveis dela.
 * @param {string} type @param {string} [objectNameSingular] objeto do gatilho @param {boolean} [recordless]
 * @returns {AutomationAction}
 */
export function createDefaultAction(type, objectNameSingular = 'enrollmentOpportunity', recordless = false) {
  return { ...defaultActionFields(type, objectNameSingular, recordless), id: crypto.randomUUID() };
}

/** @param {string} type @param {string} objectNameSingular @param {boolean} recordless @returns {AutomationAction} */
function defaultActionFields(type, objectNameSingular, recordless) {
  switch (type) {
    case 'IF_ELSE':
      return { type, name: '', conditions: null, branches: { yes: [], no: [] } };
    case 'CREATE_RECORD':
      return { type, objectNameSingular, fields: {} };
    case 'UPDATE_RECORD':
      return { type, objectNameSingular, recordId: recordless ? '' : '{{record.id}}', fields: {} };
    case 'FIND_RECORDS':
      return { type, objectNameSingular: 'person', filter: null, orderBy: null, limit: 1 };
    case 'CREATE_CALENDAR_EVENT':
      return { type, eventType: 'MEETING', title: '', description: '', location: '', start: { fieldName: null, amount: 1, unit: 'DAYS' }, startsAtTemplate: null, durationMinutes: 60, organizer: recordless ? 'NONE' : 'RECORD_OWNER', organizerWorkspaceMemberId: null, linkToRecord: false };
    case 'HTTP_REQUEST':
      return { type, method: 'POST', url: '', headers: [], bodyType: 'JSON', body: '', expectedResponse: null };
    case 'CREATE_TASK':
      return { type, title: '', body: '', taskKind: null, due: { fieldName: null, amount: 1, unit: 'DAYS' }, assignee: recordless ? 'NONE' : 'RECORD_OWNER', assigneeWorkspaceMemberId: null };
    case 'ASSIGN_OWNER':
      return { type, fieldName: null, strategy: 'ROUND_ROBIN', workspaceMemberIds: [], onlyIfEmpty: true };
    case 'UPDATE_FIELD':
      return { type, fieldName: '', value: null };
    case 'SEND_EMAIL':
      return { type, recipient: recordless ? 'CUSTOM' : 'RECORD_CONTACT', toAddress: '', subject: '', body: '' };
    case 'SEND_NOTIFICATION':
      return { type, recipient: recordless ? 'ALL_MEMBERS' : 'RECORD_OWNER', workspaceMemberIds: [], title: '', body: '' };
    case 'SEND_WHATSAPP_TEMPLATE':
      return { type, templateId: null, recipient: 'RECORD_CONTACT', variables: {}, headerVariables: {} };
    case 'WAIT':
      return { type, mode: 'DURATION', amount: 1, unit: 'DAYS', fieldName: null, cancelIfConditionsFail: true };
    default:
      return { type: 'CALL_WEBHOOK', url: '', secret: '' };
  }
}

/** @param {any} member */
const memberName = (member) => [member.name?.firstName, member.name?.lastName].filter(Boolean).join(' ') || member.userEmail || member.id;

/**
 * @param {{ label: string, hint?: string, children: any }} props
 */
function Field({ label, hint, children }) {
  return html`<label class="form-field">
    <span class="form-field-label">${label}</span>
    ${children}
    ${hint ? html`<span class="input-hint">${hint}</span>` : null}
  </label>`;
}

/**
 * @param {{ members: any[], selected: string[], disabled: boolean, onChange: (ids: string[]) => void }} props
 */
function MemberPicker({ members, selected, disabled, onChange }) {
  const selectedSet = new Set(selected);
  return html`<div class="automation-chip-picker">
    ${members.map((member) => html`<button type="button" key=${member.id} class="automation-chip" aria-pressed=${selectedSet.has(member.id) ? 'true' : 'false'} disabled=${disabled}
      onClick=${() => onChange(selectedSet.has(member.id) ? selected.filter((id) => id !== member.id) : [...selected, member.id])}>${memberName(member)}</button>`)}
  </div>`;
}

/**
 * @param {{ field: FieldMetadata | undefined, value: any, members: any[], disabled: boolean, onChange: (value: any) => void }} props
 */
function FieldValueInput({ field, value, members, disabled, onChange }) {
  if (!field) return html`<input class="text-input" disabled=${true} />`;
  if (field.type === 'SELECT') {
    return html`<select class="text-input" value=${value ?? ''} disabled=${disabled} onChange=${(event) => onChange(event.currentTarget.value || null)}>
      <option value="">${t('automations.form.emptyValue')}</option>
      ${(field.options ?? []).map((option) => html`<option key=${option.value} value=${option.value}>${translateMetadataLabel(option.label)}</option>`)}
    </select>`;
  }
  if (field.type === 'RELATION') {
    return html`<select class="text-input" value=${value ?? ''} disabled=${disabled} onChange=${(event) => onChange(event.currentTarget.value || null)}>
      <option value="">${t('automations.form.emptyValue')}</option>
      ${members.map((member) => html`<option key=${member.id} value=${member.id}>${memberName(member)}</option>`)}
    </select>`;
  }
  if (field.type === 'BOOLEAN') {
    return html`<${Toggle} value=${Boolean(value)} onChange=${disabled ? undefined : onChange} ariaLabel=${translateMetadataLabel(field.label)} />`;
  }
  if (field.type === 'RATING') {
    return html`<select class="text-input" value=${value ?? ''} disabled=${disabled} onChange=${(event) => onChange(event.currentTarget.value || null)}>
      <option value="">${t('automations.form.emptyValue')}</option>
      ${[1, 2, 3, 4, 5].map((rating) => html`<option key=${rating} value=${`RATING_${rating}`}>${'★'.repeat(rating)}</option>`)}
    </select>`;
  }
  const inputType = field.type === 'NUMBER' ? 'number' : field.type === 'DATE' ? 'date' : 'text';
  return html`<input class="text-input" type=${inputType} value=${value ?? ''} disabled=${disabled}
    placeholder=${field.type === 'DATE_TIME' ? '2027-01-31T12:00:00.000Z' : ''}
    onInput=${(event) => onChange(field.type === 'NUMBER' ? (event.currentTarget.value === '' ? null : Number(event.currentTarget.value)) : event.currentTarget.value)} />`;
}

/** @typedef {import('../../data/integrations.js').WhatsappTemplate} WhatsappTemplate */

/**
 * Variáveis que cabem num campo do modelo: contato (a pessoa que recebe), registro e da instituição.
 * @param {ObjectMetadata | undefined} objectMetadata
 */
function templateVariableGroups(objectMetadata) {
  const personMetadata = objectsByNameSingular.value.get('person');
  /** @param {ObjectMetadata | undefined} metadata @param {string} root */
  const tokensOf = (metadata, root) => pickFields(metadata, (field) => isWatchableField(field) && !['RAW_JSON', 'FILES', 'RICH_TEXT', 'UUID'].includes(field.type))
    .flatMap((field) => {
      const label = translateMetadataLabel(field.label);
      const key = field.type === 'RELATION' ? field.name : recordKeyOf(field);
      if (field.type === 'FULL_NAME') return [{ token: `{{${root}.${key}.firstName}}`, label: `${label} (primeiro nome)` }, { token: `{{${root}.${key}}}`, label }];
      return [{ token: `{{${root}.${key}}}`, label }];
    });
  return [
    { label: t('automations.form.whatsappContactFields'), items: tokensOf(personMetadata, 'contact') },
    ...(objectMetadata?.nameSingular === 'person' ? [] : [{ label: t('automations.form.whatsappRecordFields'), items: tokensOf(objectMetadata, 'record') }]),
    { label: t('automations.form.whatsappOtherFields'), items: [{ token: '{{institution.name}}', label: t('automations.form.variableInstitution') }, { token: '{{recordUrl}}', label: t('automations.form.variableRecordUrl') }] },
  ];
}

/**
 * Prévia com um registro de verdade: texto preenchido, quem recebe e o que faria a execução falhar.
 * @param {{ objectMetadata: ObjectMetadata | undefined, action: AutomationAction }} props
 */
function TemplatePreview({ objectMetadata, action }) {
  const [preview, setPreview] = useState(/** @type {import('../../data/automations.js').TemplateActionPreview | null} */ (null));
  const [reloadKey, setReloadKey] = useState(0);
  const request = useDebouncedValue(JSON.stringify({ templateId: action.templateId, variables: action.variables, headerVariables: action.headerVariables }), 500);
  useEffect(() => {
    if (!objectMetadata || !action.templateId) {
      setPreview(null);
      return undefined;
    }
    let isCancelled = false;
    previewAutomationAction({ objectNameSingular: objectMetadata.nameSingular, action: { ...action, type: 'SEND_WHATSAPP_TEMPLATE' } })
      .then((result) => {
        if (!isCancelled) setPreview(result);
      })
      .catch(() => {
        if (!isCancelled) setPreview(null);
      });
    return () => {
      isCancelled = true;
    };
  }, [objectMetadata?.nameSingular, request, reloadKey]);
  if (!action.templateId || !preview) return null;
  return html`<div class="automation-template-preview" data-testid="whatsapp-template-preview">
    <div class="automation-template-preview-header">
      <span class="form-field-label">${t('automations.form.whatsappPreview')}</span>
      ${preview.record && preview.contact ? html`<span class="automation-muted">${t('automations.form.whatsappPreviewWith', { record: preview.record.label, contact: preview.contact.label })}</span>` : null}
      <${LightIconButton} icon="IconRefresh" ariaLabel=${t('automations.form.whatsappPreviewRefresh')} onClick=${() => setReloadKey(reloadKey + 1)} />
    </div>
    ${!preview.record
      ? html`<p class="automation-muted">${t('automations.form.whatsappPreviewNoRecord')}</p>`
      : html`<div class="automation-template-bubble">${preview.text}</div>`}
    ${preview.record && !preview.contact ? html`<p class="automation-template-warning"><${Icon} name="IconAlertTriangle" size=${14} />${t('automations.form.whatsappPreviewNoContact')}</p>` : null}
    ${preview.missingVariables.length > 0
      ? html`<p class="automation-template-warning"><${Icon} name="IconAlertTriangle" size=${14} />${t('automations.form.whatsappPreviewMissing', { variables: preview.missingVariables.map((name) => `{{${name}}}`).join(', ') })}</p>`
      : null}
    ${preview.marketingBlock ? html`<p class="automation-template-warning"><${Icon} name="IconAlertTriangle" size=${14} />${preview.marketingBlock}</p>` : null}
  </div>`;
}

/**
 * Opção do modelo: nome, categoria traduzida e, com mais de um número, o número.
 * @param {WhatsappTemplate} template @param {any[]} whatsappChannels
 */
function templateOptionLabel(template, whatsappChannels) {
  const details = [template.category ? templateCategoryLabel(template.category) : null];
  if (whatsappChannels.length > 1) details.push(channelLabel(whatsappChannels.find((channel) => channel.id === template.channelId)) || null);
  const extra = details.filter(Boolean).join(', ');
  return extra ? `${template.name} (${extra})` : template.name;
}

/**
 * Modelo aprovado do canal e o valor de cada variável ({{1}}, {{2}} ou nomeadas), com campos para inserir e prévia.
 * @param {{ action: AutomationAction, objectMetadata: ObjectMetadata | undefined, templates: WhatsappTemplate[] | null, disabled: boolean,
 *   set: (patch: Record<string, any>) => void, reloadTemplates?: () => void, whatsappChannels?: any[] }} props
 */
function WhatsappTemplateFields({ action, objectMetadata, templates, disabled, set, reloadTemplates, whatsappChannels = [] }) {
  const available = (templates ?? []).filter((template) => template.isSendable || template.id === action.templateId);
  const template = available.find((candidate) => candidate.id === action.templateId) ?? null;
  const groups = templateVariableGroups(objectMetadata);
  const variables = action.variables ?? {};
  const headerVariables = action.headerVariables ?? {};
  const slots = template
    ? [...template.headerVariables.map((name) => ({ part: 'headerVariables', name })), ...template.bodyVariables.map((name) => ({ part: 'variables', name }))]
    : [];
  const setVariable = (/** @type {string} */ part, /** @type {string} */ name, /** @type {string} */ value) => set({ [part]: { ...(part === 'variables' ? variables : headerVariables), [name]: value } });
  return html`
    <div class="form-field-row">
      <${Field} label=${t('automations.form.whatsappTemplate')} hint=${templates && available.length === 0 ? t('automations.form.whatsappNoTemplates') : !action.templateId ? t('automations.form.whatsappTemplateRequired') : undefined}>
        <select class="text-input" value=${action.templateId ?? ''} disabled=${disabled} data-testid="whatsapp-template-select"
          onChange=${(event) => set({ templateId: event.currentTarget.value || null })}>
          <option value="">${t('automations.form.whatsappChooseTemplate')}</option>
          ${available.map((candidate) => html`<option key=${candidate.id} value=${candidate.id}>${templateOptionLabel(candidate, whatsappChannels)}</option>`)}
        </select>
      <//>
      <${Field} label=${t('automations.form.whatsappRecipient')}>
        <input class="text-input" value=${t('automations.form.whatsappRecipientValue')} disabled=${true} />
      <//>
    </div>
    <div class="automation-template-links">
      <a class="integration-link" href=${WHATSAPP_TEMPLATES_PATH} target="_blank" rel="noopener">
        <${Icon} name="IconExternalLink" size=${14} />${t('whatsappTemplates.actions.manage')}<span class="visually-hidden-text"> (${t('whatsappTemplates.shortcut.opensInNewTab')})</span>
      </a>
      ${reloadTemplates ? html`<${Button} title=${t('whatsappTemplates.actions.refreshList')} icon="IconRefresh" variant="secondary" size="small" onClick=${reloadTemplates} />` : null}
    </div>
    ${template?.category === 'MARKETING' ? html`<p class="automation-muted"><${Icon} name="IconShieldCheck" size=${14} /> ${t('automations.form.whatsappMarketingHint')}</p>` : null}
    ${slots.length > 0
      ? html`<div class="form-field">
          <span class="form-field-label">${t('automations.form.whatsappVariables')}</span>
          <span class="input-hint">${t('automations.form.whatsappVariablesHint')}</span>
          ${slots.map((slot) => {
            const value = slot.part === 'variables' ? variables[slot.name] ?? '' : headerVariables[slot.name] ?? variables[slot.name] ?? '';
            return html`<div class="automation-variable-row" key=${`${slot.part}-${slot.name}`}>
              <span class="automation-variable-name">{{${slot.name}}}</span>
              <input class="text-input" value=${value} disabled=${disabled} aria-label=${t('automations.form.whatsappVariable', { name: `{{${slot.name}}}` })}
                onInput=${(event) => setVariable(slot.part, slot.name, event.currentTarget.value)} />
              <select class="text-input automation-variable-insert" value="" disabled=${disabled} aria-label=${t('automations.form.whatsappInsertField')}
                onChange=${(event) => {
                  const token = event.currentTarget.value;
                  event.currentTarget.value = '';
                  if (token) setVariable(slot.part, slot.name, `${value}${token}`);
                }}>
                <option value="">${t('automations.form.whatsappInsertField')}</option>
                ${groups.map((group) => html`<optgroup key=${group.label} label=${group.label}>
                  ${group.items.map((item) => html`<option key=${item.token} value=${item.token}>${item.label}</option>`)}
                </optgroup>`)}
              </select>
            </div>`;
          })}
        </div>`
      : null}
    <p class="automation-muted">${t('automations.form.whatsappRepeatHint')}</p>
    <${TemplatePreview} objectMetadata=${objectMetadata} action=${action} />`;
}

/**
 * Espera entre ações: um tempo fixo ou até uma data do registro (com antecedência), e se cancela quando a condição cai.
 * @param {{ action: AutomationAction, objectMetadata: ObjectMetadata | undefined, disabled: boolean, set: (patch: Record<string, any>) => void }} props
 */
function WaitFields({ action, objectMetadata, disabled, set }) {
  const dateFields = pickFields(objectMetadata, isScheduleDateField);
  const isUntilField = action.mode === 'UNTIL_FIELD';
  return html`
    <div class="form-field-row automation-due-row">
      <${Field} label=${t('automations.form.waitMode')}>
        <select class="text-input" value=${action.mode ?? 'DURATION'} disabled=${disabled} data-testid="wait-mode"
          onChange=${(event) => {
            const mode = event.currentTarget.value;
            set(mode === 'UNTIL_FIELD' ? { mode, fieldName: dateFields[0]?.name ?? null, amount: -24, unit: 'HOURS' } : { mode, fieldName: null, amount: 1, unit: 'DAYS' });
          }}>
          ${['DURATION', 'UNTIL_FIELD'].map((mode) => html`<option key=${mode} value=${mode} disabled=${mode === 'UNTIL_FIELD' && dateFields.length === 0}>${t(`automations.form.waitModes.${mode}`)}</option>`)}
        </select>
      <//>
      <${Field} label=${isUntilField ? t('automations.form.waitOffset') : t('automations.form.waitAmount')} hint=${isUntilField ? t('automations.form.waitOffsetHint') : undefined}>
        <input class="text-input" type="number" min=${isUntilField ? -365 : 1} max="365" value=${action.amount ?? 1} disabled=${disabled}
          onInput=${(event) => set({ amount: Number(event.currentTarget.value) })} />
      <//>
      <${Field} label=${t('automations.form.unit')}>
        <select class="text-input" value=${action.unit ?? 'DAYS'} disabled=${disabled} onChange=${(event) => set({ unit: event.currentTarget.value })}>
          ${['MINUTES', 'HOURS', 'DAYS'].map((unit) => html`<option key=${unit} value=${unit}>${t(`automations.units.${unit}`, { count: 2 })}</option>`)}
        </select>
      <//>
    </div>
    ${isUntilField
      ? html`<${Field} label=${t('automations.form.waitField')}>
          <select class="text-input" value=${action.fieldName ?? ''} disabled=${disabled} onChange=${(event) => set({ fieldName: event.currentTarget.value })}>
            ${dateFields.map((field) => html`<option key=${field.id} value=${field.name}>${translateMetadataLabel(field.label)}</option>`)}
          </select>
        <//>`
      : null}
    <label class="automation-inline-toggle">
      <${Toggle} value=${action.cancelIfConditionsFail !== false} onChange=${disabled ? undefined : (value) => set({ cancelIfConditionsFail: value })} ariaLabel=${t('automations.form.waitCancel')} />
      <span>${t('automations.form.waitCancel')}</span>
    </label>
    <span class="input-hint">${t('automations.form.waitCancelHint')}</span>`;
}

/**
 * Contexto que desce pela árvore: variáveis por passo, membros, modelos e o gatilho.
 * @typedef {{ scope: import('./automation-variables.js').VariableScope, members: any[], templates: WhatsappTemplate[] | null, disabled: boolean,
 *   triggerType: string, triggerSettings: Record<string, any>, reloadTemplates?: () => void, whatsappChannels?: any[] }} EditorContext
 */

/** @param {any} member */
const memberLabel = (member) => [member.name?.firstName, member.name?.lastName].filter(Boolean).join(' ') || member.userEmail || member.id;

/**
 * Valor de um campo numa ação genérica: seleção, membro, sim ou não, nome completo ou texto com variáveis.
 * @param {{ field: FieldMetadata | undefined, value: any, members: any[], disabled: boolean, groups: import('./automation-variables.js').VariableGroup[],
 *   onChange: (value: any) => void }} props
 */
function RecordFieldValue({ field, value, members, disabled, groups, onChange }) {
  const label = field ? translateMetadataLabel(field.label) : t('automations.form.newValue');
  const asText = (/** @type {any} */ current) => (typeof current === 'string' || typeof current === 'number' ? String(current) : '');
  if (!field) return html`<input class="text-input" disabled=${true} aria-label=${label} />`;
  if (field.type === 'SELECT' && !(typeof value === 'string' && value.includes('{{'))) {
    return html`<select class="text-input" value=${value ?? ''} disabled=${disabled} aria-label=${label} onChange=${(event) => onChange(event.currentTarget.value || null)}>
      <option value="">${t('automations.form.emptyValue')}</option>
      ${(field.options ?? []).map((option) => html`<option key=${option.value} value=${option.value}>${translateMetadataLabel(option.label)}</option>`)}
    </select>`;
  }
  if (isMemberRelation(field)) {
    return html`<select class="text-input" value=${value ?? ''} disabled=${disabled} aria-label=${label} onChange=${(event) => onChange(event.currentTarget.value || null)}>
      <option value="">${t('automations.form.emptyValue')}</option>
      ${members.map((member) => html`<option key=${member.id} value=${member.id}>${memberLabel(member)}</option>`)}
    </select>`;
  }
  if (field.type === 'BOOLEAN') {
    return html`<select class="text-input" value=${String(value ?? false)} disabled=${disabled} aria-label=${label} onChange=${(event) => onChange(event.currentTarget.value === 'true')}>
      <option value="true">${t('common.true')}</option><option value="false">${t('common.false')}</option>
    </select>`;
  }
  if (field.type === 'FULL_NAME') {
    const name = value && typeof value === 'object' ? value : {};
    return html`<div class="automation-record-field-composite">
      <${VariableTextField} label=${t('automations.recordFields.firstName')} value=${name.firstName ?? ''} groups=${groups} disabled=${disabled} onChange=${(firstName) => onChange({ ...name, firstName })} />
      <${VariableTextField} label=${t('automations.recordFields.lastName')} value=${name.lastName ?? ''} groups=${groups} disabled=${disabled} onChange=${(lastName) => onChange({ ...name, lastName })} />
    </div>`;
  }
  const subField = { EMAILS: 'primaryEmail', PHONES: 'primaryPhoneNumber', LINKS: 'primaryLinkUrl' }[/** @type {'EMAILS' | 'PHONES' | 'LINKS'} */ (field.type)];
  if (subField) {
    const composite = value && typeof value === 'object' ? value : {};
    return html`<${VariableTextField} label=${label} value=${composite[subField] ?? ''} groups=${groups} disabled=${disabled}
      onChange=${(text) => onChange(field.type === 'PHONES' ? { ...composite, primaryPhoneNumber: text, primaryPhoneCountryCode: composite.primaryPhoneCountryCode ?? 'BR' } : { ...composite, [subField]: text })} />`;
  }
  const hint = field.type === 'RELATION' ? t('automations.recordFields.relationHint')
    : field.type === 'DATE_TIME' || field.type === 'DATE' ? t('automations.recordFields.dateHint') : undefined;
  return html`<${VariableTextField} label=${label} value=${asText(value)} groups=${groups} disabled=${disabled} hint=${hint}
    onChange=${(text) => onChange(field.type === 'NUMBER' && text !== '' && !text.includes('{{') && !Number.isNaN(Number(text)) ? Number(text) : text)} />`;
}

/**
 * Campos e valores de CREATE_RECORD e UPDATE_RECORD: uma linha por campo, com o seletor de variáveis.
 * @param {{ objectMetadata: ObjectMetadata | undefined, fields: Record<string, any>, members: any[], disabled: boolean,
 *   groups: import('./automation-variables.js').VariableGroup[], onChange: (fields: Record<string, any>) => void }} props
 */
function RecordFieldsEditor({ objectMetadata, fields, members, disabled, groups, onChange }) {
  const available = pickFields(objectMetadata, isRecordActionField);
  const entries = Object.entries(fields ?? {});
  const used = new Set(entries.map(([key]) => key));
  const rename = (/** @type {string} */ from, /** @type {string} */ to) => onChange(Object.fromEntries(entries.map(([key, value]) => (key === from ? [to, null] : [key, value]))));
  return html`<div class="automation-record-fields">
    <span class="form-field-label">${t('automations.recordFields.title')}</span>
    ${entries.length === 0 ? html`<p class="automation-muted">${t('automations.recordFields.empty')}</p>` : null}
    ${entries.map(([key, value]) => {
      const field = findField(objectMetadata, key);
      return html`<div class="automation-record-field-row" key=${key}>
        <select class="text-input" value=${key} disabled=${disabled} aria-label=${t('automations.form.field')} onChange=${(event) => rename(key, event.currentTarget.value)}>
          ${available.filter((candidate) => recordKeyOf(candidate) === key || !used.has(recordKeyOf(candidate)))
            .map((candidate) => html`<option key=${candidate.id} value=${recordKeyOf(candidate)}>${translateMetadataLabel(candidate.label)}</option>`)}
        </select>
        <${RecordFieldValue} field=${field} value=${value} members=${members} disabled=${disabled} groups=${groups} onChange=${(next) => onChange({ ...fields, [key]: next })} />
        ${disabled ? null : html`<${LightIconButton} icon="IconTrash" ariaLabel=${t('automations.recordFields.remove')} onClick=${() => onChange(Object.fromEntries(entries.filter(([other]) => other !== key)))} />`}
      </div>`;
    })}
    ${disabled ? null : html`<div><${Button} title=${t('automations.recordFields.add')} icon="IconPlus" variant="secondary" size="small"
      disabled=${available.every((candidate) => used.has(recordKeyOf(candidate)))}
      onClick=${() => {
        const next = available.find((candidate) => !used.has(recordKeyOf(candidate)));
        if (next) onChange({ ...fields, [recordKeyOf(next)]: null });
      }} /></div>`}
  </div>`;
}

/** @param {{ value: string, disabled: boolean, onChange: (value: string) => void, label?: string }} props */
function ObjectSelect({ value, disabled, onChange, label = t('automations.form.object') }) {
  return html`<${Field} label=${label}>
    <select class="text-input" value=${value ?? ''} disabled=${disabled} onChange=${(event) => onChange(event.currentTarget.value)}>
      <option value="">${t('automations.form.choose')}</option>
      ${automationObjects(objectsState.value).map((candidate) => html`<option key=${candidate.id} value=${candidate.nameSingular}>${translateMetadataLabel(candidate.labelSingular)}</option>`)}
    </select>
  <//>`;
}

/**
 * Requisição HTTP: método, URL, cabeçalhos (os secretos nunca voltam da API), corpo e o exemplo da resposta.
 * @param {{ action: AutomationAction, disabled: boolean, groups: import('./automation-variables.js').VariableGroup[], set: (patch: Record<string, any>) => void }} props
 */
function HttpRequestFields({ action, disabled, groups, set }) {
  const headers = Array.isArray(action.headers) ? action.headers : [];
  const setHeader = (/** @type {number} */ index, /** @type {Record<string, any>} */ patch) => set({ headers: headers.map((header, position) => (position === index ? { ...header, ...patch } : header)) });
  const hasBody = ['POST', 'PUT', 'PATCH'].includes(action.method ?? 'GET');
  return html`
    <div class="form-field-row automation-http-row">
      <${Field} label=${t('automations.http.method')}>
        <select class="text-input" value=${action.method ?? 'GET'} disabled=${disabled} onChange=${(event) => set({ method: event.currentTarget.value })}>
          ${['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((method) => html`<option key=${method} value=${method}>${method}</option>`)}
        </select>
      <//>
      <${VariableTextField} label=${t('automations.http.url')} value=${action.url} groups=${groups} disabled=${disabled} placeholder="https://api.exemplo.com.br/endpoint"
        hint=${t('automations.http.urlHint')} testId="http-url" onChange=${(url) => set({ url })} />
    </div>
    <div class="form-field">
      <span class="form-field-label">${t('automations.http.headers')}</span>
      ${headers.map((header, index) => html`<div class="automation-header-row" key=${index}>
        <input class="text-input" value=${header.name ?? ''} disabled=${disabled} placeholder=${t('automations.http.headerName')} aria-label=${t('automations.http.headerName')}
          onInput=${(event) => setHeader(index, { name: event.currentTarget.value })} />
        <input class="text-input" type=${header.isSecret ? 'password' : 'text'} value=${header.value ?? ''} disabled=${disabled} aria-label=${t('automations.http.headerValue')}
          placeholder=${header.isSecret && header.hasValue ? t('automations.form.secretKept') : t('automations.http.headerValue')}
          onInput=${(event) => setHeader(index, { value: event.currentTarget.value })} />
        <label class="automation-inline-toggle">
          <${Toggle} value=${Boolean(header.isSecret)} onChange=${disabled ? undefined : (isSecret) => setHeader(index, { isSecret })} ariaLabel=${t('automations.http.secret')} />
          <span>${t('automations.http.secret')}</span>
        </label>
        ${disabled ? null : html`<${LightIconButton} icon="IconTrash" ariaLabel=${t('automations.http.removeHeader')} onClick=${() => set({ headers: headers.filter((_header, position) => position !== index) })} />`}
      </div>`)}
      ${disabled ? null : html`<div><${Button} title=${t('automations.http.addHeader')} icon="IconPlus" variant="secondary" size="small"
        onClick=${() => set({ headers: [...headers, { name: '', value: '', isSecret: false }] })} /></div>`}
      <span class="input-hint">${t('automations.http.secretHint')}</span>
    </div>
    ${hasBody
      ? html`<${Field} label=${t('automations.http.bodyType')}>
          <select class="text-input" value=${action.bodyType ?? 'JSON'} disabled=${disabled} onChange=${(event) => set({ bodyType: event.currentTarget.value })}>
            ${['JSON', 'TEXT', 'NONE'].map((bodyType) => html`<option key=${bodyType} value=${bodyType}>${t(`automations.http.bodyTypes.${bodyType}`)}</option>`)}
          </select>
        <//>
        ${(action.bodyType ?? 'JSON') !== 'NONE'
          ? html`<${VariableTextField} label=${t('automations.http.body')} value=${action.body} groups=${groups} disabled=${disabled} multiline=${true}
              placeholder='{"nome": "{{record.name}}"}' hint=${t('automations.http.bodyHint')} testId="http-body" onChange=${(body) => set({ body })} />`
          : null}`
      : null}
    <label class="form-field">
      <span class="form-field-label">${t('automations.http.expectedResponse')}</span>
      <textarea class="text-input automation-textarea" rows="3" value=${action.expectedResponse ?? ''} disabled=${disabled} placeholder='{"codigo": "A-1"}'
        onInput=${(event) => set({ expectedResponse: event.currentTarget.value || null })}></textarea>
      <span class="input-hint">${t('automations.http.expectedResponseHint')}</span>
    </label>`;
}

/**
 * Evento de agenda: tipo, título, início (agora ou data do registro, com deslocamento, ou uma variável), duração,
 * organizador e o vínculo com o card.
 * @param {{ action: AutomationAction, objectMetadata: ObjectMetadata | undefined, members: any[], disabled: boolean, recordless: boolean,
 *   groups: import('./automation-variables.js').VariableGroup[], set: (patch: Record<string, any>) => void }} props
 */
function CalendarEventFields({ action, objectMetadata, members, disabled, recordless, groups, set }) {
  const dateFields = pickFields(objectMetadata, isScheduleDateField);
  const start = action.start ?? { fieldName: null, amount: 1, unit: 'DAYS' };
  const startMode = action.startsAtTemplate ? 'VARIABLE' : start.fieldName ? 'FIELD' : 'NOW';
  return html`
    <div class="form-field-row">
      <${Field} label=${t('automations.calendar.eventType')}>
        <select class="text-input" value=${action.eventType ?? 'OTHER'} disabled=${disabled} onChange=${(event) => set({ eventType: event.currentTarget.value })}>
          ${CALENDAR_EVENT_TYPES.map((type) => html`<option key=${type.key} value=${type.key}>${type.label}</option>`)}
        </select>
      <//>
      <${Field} label=${t('automations.calendar.duration')}>
        <input class="text-input" type="number" min="5" max="1440" value=${action.durationMinutes ?? 60} disabled=${disabled} onInput=${(event) => set({ durationMinutes: Number(event.currentTarget.value) })} />
      <//>
    </div>
    <${VariableTextField} label=${t('automations.calendar.title')} value=${action.title} groups=${groups} disabled=${disabled} placeholder=${t('automations.calendar.titlePlaceholder')} onChange=${(title) => set({ title })} />
    <div class="form-field-row automation-due-row">
      <${Field} label=${t('automations.calendar.start')}>
        <select class="text-input" value=${startMode} disabled=${disabled} onChange=${(event) => {
          const mode = event.currentTarget.value;
          if (mode === 'VARIABLE') set({ startsAtTemplate: '{{trigger.body.data}}' });
          else set({ startsAtTemplate: null, start: { fieldName: mode === 'FIELD' ? dateFields[0]?.name ?? null : null, amount: start.amount ?? 1, unit: start.unit ?? 'DAYS' } });
        }}>
          ${['NOW', 'FIELD', 'VARIABLE'].map((mode) => html`<option key=${mode} value=${mode} disabled=${mode === 'FIELD' && (recordless || dateFields.length === 0)}>${t(`automations.calendar.startModes.${mode}`)}</option>`)}
        </select>
      <//>
      ${startMode === 'VARIABLE'
        ? html`<${VariableTextField} label=${t('automations.calendar.startsAt')} value=${action.startsAtTemplate} groups=${groups} disabled=${disabled} hint=${t('automations.recordFields.dateHint')}
            onChange=${(startsAtTemplate) => set({ startsAtTemplate })} />`
        : html`<${Field} label=${t('automations.form.amount')} hint=${startMode === 'FIELD' ? t('automations.form.negativeHint') : undefined}>
              <input class="text-input" type="number" value=${start.amount ?? 0} disabled=${disabled} onInput=${(event) => set({ start: { ...start, amount: Number(event.currentTarget.value) } })} />
            <//>
            <${Field} label=${t('automations.form.unit')}>
              <select class="text-input" value=${start.unit ?? 'DAYS'} disabled=${disabled} onChange=${(event) => set({ start: { ...start, unit: event.currentTarget.value } })}>
                ${['MINUTES', 'HOURS', 'DAYS'].map((unit) => html`<option key=${unit} value=${unit}>${t(`automations.units.${unit}`, { count: 2 })}</option>`)}
              </select>
            <//>`}
    </div>
    ${startMode === 'FIELD'
      ? html`<${Field} label=${t('automations.form.relativeToField')}>
          <select class="text-input" value=${start.fieldName ?? ''} disabled=${disabled} onChange=${(event) => set({ start: { ...start, fieldName: event.currentTarget.value } })}>
            ${dateFields.map((field) => html`<option key=${field.id} value=${field.name}>${translateMetadataLabel(field.label)}</option>`)}
          </select>
        <//>`
      : null}
    <div class="form-field-row">
      <${Field} label=${t('automations.calendar.organizer')}>
        <select class="text-input" value=${action.organizer ?? 'RECORD_OWNER'} disabled=${disabled} onChange=${(event) => set({ organizer: event.currentTarget.value })}>
          ${['RECORD_OWNER', 'SPECIFIC', 'NONE'].map((value) => html`<option key=${value} value=${value} disabled=${value === 'RECORD_OWNER' && recordless}>${t(`automations.form.assigneeOptions.${value}`)}</option>`)}
        </select>
      <//>
      ${action.organizer === 'SPECIFIC'
        ? html`<${Field} label=${t('automations.form.member')}>
            <select class="text-input" value=${action.organizerWorkspaceMemberId ?? ''} disabled=${disabled} onChange=${(event) => set({ organizerWorkspaceMemberId: event.currentTarget.value || null })}>
              <option value="">${t('automations.form.choose')}</option>
              ${members.map((member) => html`<option key=${member.id} value=${member.id}>${memberLabel(member)}</option>`)}
            </select>
          <//>`
        : html`<span></span>`}
    </div>
    ${objectMetadata?.nameSingular === 'enrollmentOpportunity' && !recordless
      ? html`<label class="automation-inline-toggle">
          <${Toggle} value=${action.linkToRecord === true} onChange=${disabled ? undefined : (linkToRecord) => set({ linkToRecord })} ariaLabel=${t('automations.calendar.linkToRecord')} />
          <span>${t('automations.calendar.linkToRecord')}</span>
        </label>
        <span class="input-hint">${t('automations.calendar.linkToRecordHint')}</span>`
      : null}
    <${VariableTextField} label=${t('automations.calendar.location')} value=${action.location} groups=${groups} disabled=${disabled} onChange=${(location) => set({ location })} />
    <${VariableTextField} label=${t('automations.calendar.description')} value=${action.description} groups=${groups} disabled=${disabled} multiline=${true} onChange=${(description) => set({ description })} />`;
}

/**
 * @param {{ action: AutomationAction, objectMetadata: ObjectMetadata | undefined, context: EditorContext, depth: number, onChange: (action: AutomationAction) => void }} props
 */
function ActionFields({ action, objectMetadata, context, depth, onChange }) {
  const { members, templates, disabled, scope, reloadTemplates, whatsappChannels } = context;
  const recordless = isRecordlessTrigger(context.triggerType, context.triggerSettings);
  const groups = buildVariableGroups(scope, action.id ?? null);
  const set = (/** @type {Record<string, any>} */ patch) => onChange({ ...action, ...patch });
  const text = (/** @type {string} */ key, /** @type {string} */ label, /** @type {{ multiline?: boolean, placeholder?: string, hint?: string, type?: string }} */ options = {}) => html`<${VariableTextField}
    label=${label} value=${action[key]} groups=${groups} disabled=${disabled} multiline=${options.multiline} placeholder=${options.placeholder} hint=${options.hint} type=${options.type}
    onChange=${(value) => set({ [key]: value })} />`;
  const dateFields = pickFields(objectMetadata, isScheduleDateField);
  switch (action.type) {
    case 'IF_ELSE':
      return html`<${IfElseFields} action=${action} objectMetadata=${objectMetadata} context=${context} depth=${depth} groups=${groups} set=${set} />`;
    case 'CREATE_RECORD':
    case 'UPDATE_RECORD': {
      const target = objectsByNameSingular.value.get(action.objectNameSingular ?? '');
      return html`
        <div class="form-field-row">
          <${ObjectSelect} value=${action.objectNameSingular} disabled=${disabled} onChange=${(objectNameSingular) => set({ objectNameSingular, fields: {} })} />
          ${action.type === 'UPDATE_RECORD'
            ? html`<${VariableTextField} label=${t('automations.recordFields.recordId')} value=${action.recordId} groups=${groups} disabled=${disabled}
                placeholder="{{record.id}}" hint=${t('automations.recordFields.recordIdHint')} onChange=${(recordId) => set({ recordId })} />`
            : html`<span></span>`}
        </div>
        <${RecordFieldsEditor} objectMetadata=${target} fields=${action.fields ?? {}} members=${members} disabled=${disabled} groups=${groups} onChange=${(fields) => set({ fields })} />`;
    }
    case 'FIND_RECORDS': {
      const target = objectsByNameSingular.value.get(action.objectNameSingular ?? '');
      const rows = parseConditions(action.filter);
      const sortFields = pickFields(target, (field) => isWatchableField(field) && field.type !== 'RELATION');
      return html`
        <div class="form-field-row">
          <${ObjectSelect} value=${action.objectNameSingular} disabled=${disabled} onChange=${(objectNameSingular) => set({ objectNameSingular, filter: null, orderBy: null })} />
          <${Field} label=${t('automations.find.limit')} hint=${t('automations.find.limitHint')}>
            <input class="text-input" type="number" min="1" max="50" value=${action.limit ?? 1} disabled=${disabled} onInput=${(event) => set({ limit: Number(event.currentTarget.value) })} />
          <//>
        </div>
        <div class="form-field">
          <span class="form-field-label">${t('automations.find.filter')}</span>
          <${ConditionsEditor} objectMetadata=${target} rows=${rows} rawConditions=${action.filter} members=${members} disabled=${disabled} variableGroups=${groups}
            onChange=${(nextRows) => set({ filter: serializeConditions(nextRows) })} />
        </div>
        <div class="form-field-row">
          <${Field} label=${t('automations.find.sortField')}>
            <select class="text-input" value=${action.orderBy?.field ?? ''} disabled=${disabled}
              onChange=${(event) => set({ orderBy: event.currentTarget.value ? { field: event.currentTarget.value, direction: action.orderBy?.direction ?? 'ASC' } : null })}>
              <option value="">${t('automations.find.noSort')}</option>
              ${sortFields.map((field) => html`<option key=${field.id} value=${field.name}>${translateMetadataLabel(field.label)}</option>`)}
            </select>
          <//>
          <${Field} label=${t('automations.find.direction')}>
            <select class="text-input" value=${action.orderBy?.direction ?? 'ASC'} disabled=${disabled || !action.orderBy} onChange=${(event) => set({ orderBy: { ...action.orderBy, direction: event.currentTarget.value } })}>
              <option value="ASC">${t('automations.find.ascending')}</option><option value="DESC">${t('automations.find.descending')}</option>
            </select>
          <//>
        </div>
        <span class="input-hint">${t('automations.find.outputHint')}</span>`;
    }
    case 'CREATE_CALENDAR_EVENT':
      return html`<${CalendarEventFields} action=${action} objectMetadata=${objectMetadata} members=${members} disabled=${disabled} recordless=${recordless} groups=${groups} set=${set} />`;
    case 'HTTP_REQUEST':
      return html`<${HttpRequestFields} action=${action} disabled=${disabled} groups=${groups} set=${set} />`;
    case 'CREATE_TASK': {
      const taskKindField = objectsByNameSingular.value.get('task')?.fields.find((field) => field.name === 'taskKind');
      const due = action.due ?? null;
      const dueMode = !due ? 'NONE' : due.fieldName ? 'FIELD' : 'NOW';
      return html`
        ${text('title', t('automations.form.taskTitle'), { placeholder: t('automations.form.taskTitlePlaceholder') })}
        ${text('body', t('automations.form.taskBody'), { multiline: true })}
        <div class="form-field-row">
          <${Field} label=${t('automations.form.taskKind')}>
            <select class="text-input" value=${action.taskKind ?? ''} disabled=${disabled} onChange=${(event) => set({ taskKind: event.currentTarget.value || null })}>
              <option value="">${t('automations.form.emptyValue')}</option>
              ${(taskKindField?.options ?? []).map((option) => html`<option key=${option.value} value=${option.value}>${translateMetadataLabel(option.label)}</option>`)}
            </select>
          <//>
          <${Field} label=${t('automations.form.assignee')}>
            <select class="text-input" value=${action.assignee ?? 'RECORD_OWNER'} disabled=${disabled} onChange=${(event) => set({ assignee: event.currentTarget.value })}>
              ${['RECORD_OWNER', 'SPECIFIC', 'NONE'].map((value) => html`<option key=${value} value=${value} disabled=${value === 'RECORD_OWNER' && recordless}>${t(`automations.form.assigneeOptions.${value}`)}</option>`)}
            </select>
          <//>
        </div>
        ${action.assignee === 'SPECIFIC'
          ? html`<${Field} label=${t('automations.form.member')}>
              <select class="text-input" value=${action.assigneeWorkspaceMemberId ?? ''} disabled=${disabled} onChange=${(event) => set({ assigneeWorkspaceMemberId: event.currentTarget.value || null })}>
                <option value="">${t('automations.form.choose')}</option>
                ${members.map((member) => html`<option key=${member.id} value=${member.id}>${memberName(member)}</option>`)}
              </select>
            <//>`
          : null}
        <div class="form-field-row automation-due-row">
          <${Field} label=${t('automations.form.due')}>
            <select class="text-input" value=${dueMode} disabled=${disabled} onChange=${(event) => {
              const mode = event.currentTarget.value;
              set({ due: mode === 'NONE' ? null : { fieldName: mode === 'FIELD' ? dateFields[0]?.name ?? null : null, amount: due?.amount ?? 1, unit: due?.unit ?? 'DAYS' } });
            }}>
              ${['NONE', 'NOW', 'FIELD'].map((value) => html`<option key=${value} value=${value} disabled=${value === 'FIELD' && (recordless || dateFields.length === 0)}>${t(`automations.form.dueModes.${value}`)}</option>`)}
            </select>
          <//>
          ${due
            ? html`<${Field} label=${t('automations.form.amount')} hint=${dueMode === 'FIELD' ? t('automations.form.negativeHint') : undefined}>
                  <input class="text-input" type="number" value=${due.amount} disabled=${disabled} onInput=${(event) => set({ due: { ...due, amount: Number(event.currentTarget.value) } })} />
                <//>
                <${Field} label=${t('automations.form.unit')}>
                  <select class="text-input" value=${due.unit} disabled=${disabled} onChange=${(event) => set({ due: { ...due, unit: event.currentTarget.value } })}>
                    ${['HOURS', 'DAYS'].map((unit) => html`<option key=${unit} value=${unit}>${t(`automations.units.${unit}`, { count: 2 })}</option>`)}
                  </select>
                <//>`
            : null}
        </div>
        ${dueMode === 'FIELD'
          ? html`<${Field} label=${t('automations.form.relativeToField')}>
              <select class="text-input" value=${due.fieldName ?? ''} disabled=${disabled} onChange=${(event) => set({ due: { ...due, fieldName: event.currentTarget.value } })}>
                ${dateFields.map((field) => html`<option key=${field.id} value=${field.name}>${translateMetadataLabel(field.label)}</option>`)}
              </select>
            <//>`
          : null}`;
    }
    case 'ASSIGN_OWNER': {
      const memberFields = pickFields(objectMetadata, isMemberRelation);
      return html`
        <div class="form-field-row">
          <${Field} label=${t('automations.form.assignField')}>
            <select class="text-input" value=${action.fieldName ?? memberFields[0]?.name ?? ''} disabled=${disabled} onChange=${(event) => set({ fieldName: event.currentTarget.value })}>
              ${memberFields.map((field) => html`<option key=${field.id} value=${field.name}>${translateMetadataLabel(field.label)}</option>`)}
            </select>
          <//>
          <${Field} label=${t('automations.form.strategy')}>
            <select class="text-input" value=${action.strategy} disabled=${disabled} onChange=${(event) => set({ strategy: event.currentTarget.value, workspaceMemberIds: event.currentTarget.value === 'SPECIFIC' ? (action.workspaceMemberIds ?? []).slice(0, 1) : action.workspaceMemberIds })}>
              ${['ROUND_ROBIN', 'SPECIFIC'].map((value) => html`<option key=${value} value=${value}>${t(`automations.form.strategies.${value}`)}</option>`)}
            </select>
          <//>
        </div>
        <${Field} label=${action.strategy === 'SPECIFIC' ? t('automations.form.member') : t('automations.form.roundRobinMembers')}
          hint=${action.strategy === 'SPECIFIC' ? undefined : t('automations.form.roundRobinHint')}>
          <${MemberPicker} members=${members} selected=${action.workspaceMemberIds ?? []} disabled=${disabled}
            onChange=${(ids) => set({ workspaceMemberIds: action.strategy === 'SPECIFIC' ? ids.slice(-1) : ids })} />
        <//>
        <label class="automation-inline-toggle">
          <${Toggle} value=${action.onlyIfEmpty !== false} onChange=${disabled ? undefined : (value) => set({ onlyIfEmpty: value })} ariaLabel=${t('automations.form.onlyIfEmpty')} />
          <span>${t('automations.form.onlyIfEmpty')}</span>
        </label>`;
    }
    case 'UPDATE_FIELD': {
      const fields = pickFields(objectMetadata, isUpdatableField);
      const field = fields.find((candidate) => candidate.name === action.fieldName);
      return html`<div class="form-field-row">
        <${Field} label=${t('automations.form.field')}>
          <select class="text-input" value=${action.fieldName ?? ''} disabled=${disabled} onChange=${(event) => set({ fieldName: event.currentTarget.value, value: null })}>
            <option value="">${t('automations.form.chooseField')}</option>
            ${fields.map((candidate) => html`<option key=${candidate.id} value=${candidate.name}>${translateMetadataLabel(candidate.label)}</option>`)}
          </select>
        <//>
        ${field?.type === 'TEXT'
          ? text('value', t('automations.form.newValue'), { hint: t('automations.form.variablesHint') })
          : html`<${Field} label=${t('automations.form.newValue')}>
              <${FieldValueInput} field=${field} value=${action.value} members=${members} disabled=${disabled} onChange=${(value) => set({ value })} />
            <//>`}
      </div>`;
    }
    case 'SEND_EMAIL':
      return html`
        <div class="form-field-row">
          <${Field} label=${t('automations.form.recipient')}>
            <select class="text-input" value=${action.recipient} disabled=${disabled} onChange=${(event) => set({ recipient: event.currentTarget.value })}>
              ${['RECORD_CONTACT', 'RECORD_OWNER', 'CUSTOM'].map((value) => html`<option key=${value} value=${value} disabled=${value !== 'CUSTOM' && recordless}>${t(`automations.form.emailRecipients.${value}`)}</option>`)}
            </select>
          <//>
          ${action.recipient === 'CUSTOM' ? text('toAddress', t('automations.form.toAddress'), { type: 'email', placeholder: 'secretaria@escola.com.br' }) : html`<span></span>`}
        </div>
        ${text('subject', t('automations.form.subject'), { placeholder: t('automations.form.subjectPlaceholder') })}
        ${text('body', t('automations.form.emailBody'), { multiline: true, hint: t('automations.form.emailHint') })}`;
    case 'SEND_NOTIFICATION':
      return html`
        <${Field} label=${t('automations.form.recipient')}>
          <select class="text-input" value=${action.recipient} disabled=${disabled} onChange=${(event) => set({ recipient: event.currentTarget.value })}>
            ${['RECORD_OWNER', 'SPECIFIC', 'ALL_MEMBERS'].map((value) => html`<option key=${value} value=${value} disabled=${value === 'RECORD_OWNER' && recordless}>${t(`automations.form.notificationRecipients.${value}`)}</option>`)}
          </select>
        <//>
        ${action.recipient === 'SPECIFIC'
          ? html`<${Field} label=${t('automations.form.members')}>
              <${MemberPicker} members=${members} selected=${action.workspaceMemberIds ?? []} disabled=${disabled} onChange=${(ids) => set({ workspaceMemberIds: ids })} />
            <//>`
          : null}
        ${text('title', t('automations.form.notificationTitle'))}
        ${text('body', t('automations.form.notificationBody'), { multiline: true })}`;
    case 'SEND_WHATSAPP_TEMPLATE':
      return html`<${WhatsappTemplateFields} action=${action} objectMetadata=${objectMetadata} templates=${templates} disabled=${disabled} set=${set}
        reloadTemplates=${reloadTemplates} whatsappChannels=${whatsappChannels ?? []} />`;
    case 'WAIT':
      return html`<${WaitFields} action=${action} objectMetadata=${objectMetadata} disabled=${disabled} set=${set} />`;
    default:
      return html`
        ${text('url', t('automations.form.webhookUrl'), { type: 'url', placeholder: 'https://', hint: t('automations.form.webhookHint') })}
        <${Field} label=${t('automations.form.webhookSecret')} hint=${t('automations.form.webhookSecretHint')}>
          <input class="text-input" type="password" value=${action.secret ?? ''} disabled=${disabled} placeholder=${action.hasSecret ? t('automations.form.secretKept') : ''}
            onInput=${(event) => set({ secret: event.currentTarget.value })} />
        <//>`;
  }
}

/**
 * Se/senão: condições sobre o registro e sobre variáveis, e os ramos "sim" e "não" com recuo.
 * @param {{ action: AutomationAction, objectMetadata: ObjectMetadata | undefined, context: EditorContext, depth: number,
 *   groups: import('./automation-variables.js').VariableGroup[], set: (patch: Record<string, any>) => void }} props
 */
function IfElseFields({ action, objectMetadata, context, depth, groups, set }) {
  const rows = parseConditions(action.conditions);
  const branches = action.branches ?? { yes: [], no: [] };
  const recordless = isRecordlessTrigger(context.triggerType, context.triggerSettings);
  return html`
    <${VariableTextField} label=${t('automations.ifElse.name')} value=${action.name} groups=${[]} disabled=${context.disabled} placeholder=${t('automations.ifElse.namePlaceholder')}
      onChange=${(name) => set({ name })} />
    <div class="form-field">
      <span class="form-field-label">${t('automations.ifElse.conditions')}</span>
      <${ConditionsEditor} objectMetadata=${recordless ? undefined : objectMetadata} rows=${rows} rawConditions=${action.conditions} members=${context.members} disabled=${context.disabled}
        variableGroups=${groups} allowVariableFields=${true} onChange=${(nextRows) => set({ conditions: serializeConditions(nextRows) })} />
    </div>
    ${['yes', 'no'].map((branch) => html`<div class="automation-branch" data-branch=${branch} key=${branch} data-testid=${`branch-${branch}`}>
      <div class="automation-branch-label">
        <${Icon} name=${branch === 'yes' ? 'IconCircleCheck' : 'IconCircleOff'} size=${14} />
        <span>${t(`automations.ifElse.${branch}Title`)}</span>
      </div>
      <${ActionsEditor} actions=${branches[branch] ?? []} objectMetadata=${objectMetadata} context=${context} depth=${depth + 1}
        onChange=${(actions) => set({ branches: { ...branches, [branch]: actions } })} />
    </div>`)}`;
}

/**
 * Lista de ações (a raiz ou um ramo de se/senão). O se/senão só aparece até o nível permitido.
 * @param {{ actions: AutomationAction[], objectMetadata: ObjectMetadata | undefined, context: EditorContext, depth?: number,
 *   onChange: (actions: AutomationAction[]) => void }} props
 */
function ActionsEditor({ actions, objectMetadata, context, depth = 0, onChange }) {
  const { disabled, triggerType, triggerSettings } = context;
  const recordless = isRecordlessTrigger(triggerType, triggerSettings);
  const numbers = stepNumbers(context.scope.actions);
  const actionTypes = ACTION_TYPES.filter((type) => (triggerType !== 'RECORD_DELETED' || !ACTIONS_BLOCKED_ON_DELETED_RECORD.has(type))
    && (!recordless || !ACTIONS_NEEDING_RECORD.has(type)) && (type !== 'IF_ELSE' || depth < MAXIMUM_BRANCH_DEPTH));
  const replace = (/** @type {number} */ index, /** @type {AutomationAction} */ action) => onChange(actions.map((item, position) => (position === index ? action : item)));
  const move = (/** @type {number} */ index, /** @type {number} */ offset) => {
    const next = [...actions];
    const [item] = next.splice(index, 1);
    next.splice(index + offset, 0, item);
    onChange(next);
  };
  return html`<div class="automation-actions" data-depth=${depth}>
    ${depth > 0 && actions.length === 0 ? html`<p class="automation-muted">${t('automations.ifElse.emptyBranch')}</p>` : null}
    ${actions.map((action, index) => html`<div class="automation-action-card" key=${action.id ?? index} data-type=${action.type}>
      <div class="automation-action-header">
        <span class="automation-action-number">${numbers.get(action.id ?? '') ?? index + 1}</span>
        <${Icon} name=${ACTION_ICONS[/** @type {keyof typeof ACTION_ICONS} */ (action.type)] ?? 'IconSettingsAutomation'} size=${16} />
        <select class="text-input automation-action-type" value=${action.type} disabled=${disabled} aria-label=${t('automations.form.actionType')}
          onChange=${(event) => replace(index, { ...createDefaultAction(event.currentTarget.value, objectMetadata?.nameSingular), id: action.id })}>
          ${actionTypes.map((type) => html`<option key=${type} value=${type}>${t(`automations.actionTypes.${type}`)}</option>`)}
        </select>
        ${disabled
          ? null
          : html`<span class="automation-action-buttons">
              <${LightIconButton} icon="IconChevronUp" ariaLabel=${t('automations.form.moveUp')} disabled=${index === 0} onClick=${() => move(index, -1)} />
              <${LightIconButton} icon="IconChevronDown" ariaLabel=${t('automations.form.moveDown')} disabled=${index === actions.length - 1} onClick=${() => move(index, 1)} />
              <${LightIconButton} icon="IconTrash" ariaLabel=${t('automations.form.removeAction')} onClick=${() => onChange(actions.filter((_item, position) => position !== index))} />
            </span>`}
      </div>
      <div class="automation-action-body">
        <${ActionFields} action=${action} objectMetadata=${objectMetadata} context=${context} depth=${depth} onChange=${(next) => replace(index, next)} />
      </div>
    </div>`)}
    ${disabled ? null : html`<div><${Button} title=${depth > 0 ? t('automations.ifElse.addToBranch') : t('automations.form.addAction')} icon="IconPlus" variant="secondary" size="small"
      onClick=${() => onChange([...actions, createDefaultAction(recordless ? 'SEND_NOTIFICATION' : 'CREATE_TASK', objectMetadata?.nameSingular, recordless)])} /></div>`}
  </div>`;
}

/**
 * Editor das ações: a árvore inteira, com as variáveis de cada passo calculadas sobre ela.
 * @param {{ actions: AutomationAction[], objectMetadata: ObjectMetadata | undefined, members: any[], disabled: boolean, triggerType?: string,
 *   triggerSettings?: Record<string, any>, onChange: (actions: AutomationAction[]) => void }} props
 */
export function AutomationActionsEditor({ actions, objectMetadata, members, disabled, triggerType = 'RECORD_CREATED', triggerSettings = {}, onChange }) {
  const [templates, setTemplates] = useState(/** @type {WhatsappTemplate[] | null} */ (null));
  const [whatsappChannels, setWhatsappChannels] = useState(/** @type {any[]} */ ([]));
  const usesWhatsapp = JSON.stringify(actions).includes('"SEND_WHATSAPP_TEMPLATE"');
  const reloadTemplates = () => {
    listWhatsappTemplates({ status: 'APPROVED' }).then(setTemplates).catch(() => setTemplates([]));
  };
  useEffect(() => {
    if (!usesWhatsapp || templates) return;
    reloadTemplates();
    // com mais de um número, a opção do modelo mostra de qual número ele é
    listChannels().then((data) => setWhatsappChannels(data.channels.filter((channel) => channel.type === 'WHATSAPP'))).catch(() => setWhatsappChannels([]));
  }, [usesWhatsapp]);
  /** @type {EditorContext} */
  const context = { scope: { objectMetadata, triggerType, triggerSettings, actions }, members, templates, disabled, triggerType, triggerSettings, reloadTemplates, whatsappChannels };
  return html`<${ActionsEditor} actions=${actions} objectMetadata=${objectMetadata} context=${context} onChange=${onChange} />`;
}

// formulários de tarefa, aviso, campo, webhook e requisição HTTP dos passos dos fluxos de automação (2.0.0, fluxos.md
// seção 3.3), com o mesmo contrato de props; o editor das automações continua usando o nome interno
export { ActionFields as AutomationActionFields };
